import { ShaderProgram } from '../renderer/ShaderProgram';
import type { Renderable } from '../renderer/RenderPipeline';
import type { Camera } from '../renderer/Camera';
import type { Engine } from '../core/Engine';
import type { GameModule } from '../core/GameModule';
import type { EventBus } from '../core/EventBus';
import type { FleetState } from '../systems/FleetState';
import type { FleetConfig } from '../config/FleetConfig';
import { CameraMode } from '../input/CameraController';

import vertSrc from '../shaders/indicator.vert.glsl?raw';
import fragSrc from '../shaders/indicator.frag.glsl?raw';

/**
 * Pulsing billboard indicator at fleet position.
 * Visible when camera is far; click to enter FlightView.
 */
export class FleetIndicator implements Renderable, GameModule {
  readonly name = 'FleetIndicator';

  private _gl!: WebGL2RenderingContext;
  private _shader!: ShaderProgram;
  private _vao!: WebGLVertexArrayObject;
  private _events!: EventBus;
  private _camera!: Camera;
  private _fleet: FleetState;
  private _config: FleetConfig;
  private _time = 0;
  private _visible = false;
  private readonly _scratchScreen = { x: 0, y: 0 };
  private _cameraMode = CameraMode.Free;
  private _canvas!: HTMLCanvasElement;

  constructor(fleet: FleetState, config: FleetConfig) {
    this._fleet = fleet;
    this._config = config;
  }

  init(engine: Engine): void {
    this._gl = engine.gl;
    this._events = engine.events;
    this._canvas = engine.canvas;
    this._shader = new ShaderProgram(this._gl, vertSrc, fragSrc);
    this._buildQuad();

    this._events.on<{ mode: number }>('camera:mode-changed', (d) => {
      this._cameraMode = d.mode;
    });
    this._canvas.addEventListener('click', this._onClick);
  }

  setCamera(camera: Camera): void { this._camera = camera; }
  setVisible(v: boolean): void { this._visible = v; }

  update(dt: number): void { this._time += dt; }

  render(gl: WebGL2RenderingContext, camera: Camera): void {
    if (!this._visible || !this._fleet.active) return;
    if (this._cameraMode === CameraMode.FlightView) return;

    this._shader.use();
    this._shader.setMat4('u_projection', camera.projection.data);
    this._shader.setMat4('u_view', camera.view.data);
    this._shader.setVec3('u_worldPos',
      this._fleet.centerX, 0.9, this._fleet.centerZ);
    this._shader.setFloat('u_size', this._config.indicatorSize);
    this._shader.setVec3('u_color',
      this._config.indicatorColor[0],
      this._config.indicatorColor[1],
      this._config.indicatorColor[2]);
    this._shader.setFloat('u_time', this._time);

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE);
    gl.depthMask(false);

    gl.bindVertexArray(this._vao);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    gl.bindVertexArray(null);

    gl.depthMask(true);
    gl.disable(gl.BLEND);
  }

  dispose(): void {
    this._gl.deleteVertexArray(this._vao);
    this._shader.dispose();
    this._canvas.removeEventListener('click', this._onClick);
  }

  private _onClick = (e: MouseEvent): void => {
    if (!this._visible || !this._fleet.active) return;
    if (this._cameraMode === CameraMode.FlightView) return;
    if (!this._camera) return;

    const cx = this._fleet.centerX, cz = this._fleet.centerZ;
    const wp = this._projectToScreen(cx, 0.9, cz);
    if (!wp) return;

    const rect = this._canvas.getBoundingClientRect();
    const sx = e.clientX - rect.left, sy = e.clientY - rect.top;
    const dx = sx - wp.x, dy = sy - wp.y;
    if (dx * dx + dy * dy < 40 * 40) {
      this._events.emit('fleet:enter-flightview', null);
    }
  };

  private _projectToScreen(x: number, y: number, z: number): { x: number; y: number } | null {
    const cam = this._camera;
    const vd = cam.view.data, pd = cam.projection.data;
    const ex = vd[0]*x + vd[4]*y + vd[8]*z  + vd[12];
    const ey = vd[1]*x + vd[5]*y + vd[9]*z  + vd[13];
    const ez = vd[2]*x + vd[6]*y + vd[10]*z + vd[14];
    const ew = vd[3]*x + vd[7]*y + vd[11]*z + vd[15];
    const px = pd[0]*ex + pd[4]*ey + pd[8]*ez  + pd[12]*ew;
    const py = pd[1]*ex + pd[5]*ey + pd[9]*ez  + pd[13]*ew;
    const pw = pd[3]*ex + pd[7]*ey + pd[11]*ez + pd[15]*ew;
    if (pw <= 0) return null;
    const w = this._canvas.clientWidth, h = this._canvas.clientHeight;
    this._scratchScreen.x = (px / pw * 0.5 + 0.5) * w;
    this._scratchScreen.y = (-py / pw * 0.5 + 0.5) * h;
    return this._scratchScreen;
  }

  private _buildQuad(): void {
    const gl = this._gl;
    this._vao = gl.createVertexArray()!;
    gl.bindVertexArray(this._vao);
    const buf = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, 1,1]), gl.STATIC_DRAW);
    const loc = this._shader.getAttrib('a_position');
    if (loc >= 0) { gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0); }
    gl.bindVertexArray(null);
  }
}
